import React from 'react';
import { Check } from 'lucide-react';

interface Step {
  label: string;
  completed: boolean;
}

interface OnboardingStepIndicatorProps {
  currentStep: number;
  steps: Step[];
}

export const OnboardingStepIndicator: React.FC<OnboardingStepIndicatorProps> = ({
  currentStep,
  steps,
}) => {
  return (
    <div className="w-full">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isCompleted = step.completed;
          const isActive = index === currentStep && !isCompleted;
          const isPending = !isCompleted && !isActive;
          const isLast = index === steps.length - 1;

          return (
            <React.Fragment key={index}>
              {/* Step item */}
              <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                {/* Circle */}
                <div className="relative flex items-center justify-center">
                  {isActive && (
                    <span className="absolute inline-flex h-full w-full rounded-full bg-primary opacity-30 animate-ping" />
                  )}
                  <div
                    className={[
                      'relative w-7 h-7 rounded-full flex items-center justify-center transition-all duration-300',
                      isCompleted
                        ? 'bg-green-500 text-white'
                        : isActive
                          ? 'bg-primary text-white ring-2 ring-primary/30 ring-offset-1'
                          : 'bg-muted text-muted-foreground border border-border',
                    ].join(' ')}
                  >
                    {isCompleted ? (
                      <Check className="w-3.5 h-3.5 stroke-[2.5]" />
                    ) : (
                      <span className="text-xs font-semibold leading-none">{index + 1}</span>
                    )}
                  </div>
                </div>

                {/* Label — hidden on small screens, visible on md+ */}
                <span
                  className={[
                    'hidden md:block text-xs font-medium text-center whitespace-nowrap transition-colors duration-200',
                    isCompleted
                      ? 'text-green-600'
                      : isActive
                        ? 'text-primary'
                        : 'text-muted-foreground',
                  ].join(' ')}
                >
                  {step.label}
                </span>
              </div>

              {/* Connector line */}
              {!isLast && (
                <div className="flex-1 mx-1.5 mb-5 md:mb-5 h-px transition-colors duration-300">
                  <div
                    className={[
                      'h-full transition-all duration-500',
                      steps[index].completed ? 'bg-green-400' : 'bg-border',
                    ].join(' ')}
                  />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
